import React, { useMemo, useState } from "react";

import moment from "moment";

import {
  TFunction,
  calculateFieldContributionStatus,
  isFeatureActive,
  isSingleFieldOutcomes,
  someOutcomeHasErrorOfType,
  useAddAllotmentAcknowledgements,
  useCarbonProgramImpact,
  useGetFarmFeatureFlags,
  useGetFarmPayments,
  useGetFarmReportingYear,
  useGetFarms,
  useTranslation,
} from "utils";

import { CurrencyCode, FEATURE_FLAG_SLUGS } from "consts";

import { CARBON_RUNNER_ERROR_TYPE, CarbonProgramOutcomesResponse, PRACTICE_SLUG, Payment, SingleFieldOutcomes, ValidAggregation } from "models";

import { ExtraInfo, Flex, Form, LoadingStateCard, Option, Select, Typography } from "components";

import { ImpactPanelCollapseToggle } from "pages/app/all-field-view/components/CollapsibleImpactPanel";
import { SubmissionSuccessModalContent } from "pages/app/all-field-view/components/SubmissionSuccessModal";
import { ExpectedPayoutsCard } from "pages/app/field-timeline/components/ExpectedPayoutsCard";
import { ImpactPanelEmptyState } from "pages/app/field-timeline/components/ImpactPanelEmptyState";
import { SoilPlus3ImpactPanelErrorView } from "pages/app/field-timeline/components/SoilPlus3ImpactPanelErrorView";
import {
  calculateAllotmentPayouts,
  calculateTotalEnrolledHa,
  isFutureYear as checkIsFutureYear,
  determinePayoutStatus,
  generateMonitoringYears,
  hasDataEntryForYear,
  isPastPeriodOfPerformance,
} from "pages/app/field-timeline/utils/PayoutUtils";
import { calculateImpactPanelBodyHeight } from "pages/app/field-timeline/utils/TimelineUtils";
import ResilienceScoreCard from "pages/app/resilience-score/ResilienceScoreCard";

type SoilPlus3ImpactPanelProps = {
  carbonProgramOutcomes: CarbonProgramOutcomesResponse;
  enrolledFieldsCount?: number;
  fieldSizes: Record<string, number>;
  progress: number;
  shouldShowSubmissionSuccessModalContent?: boolean;
  onOpenBulkEnrollDrawer?: () => void;
  setNavigateToMissingData?: (value: boolean) => void;
};

// This function is used to filter carbon program outcomes to show only current and future years,
// excluding historical data that may not be relevant for current impact calculations.
export const filterFieldOutcomesByYear = (data: CarbonProgramOutcomesResponse, minYear: number = moment().year()) =>
  Object.entries(data).reduce((filteredData, [fieldId, field]) => {
    const singleField = field as SingleFieldOutcomes;
    if (!singleField?.fieldOutcomes) {
      return { ...filteredData, [fieldId]: singleField };
    }

    const filteredPractices = Object.entries(singleField.fieldOutcomes).reduce((practices, [practiceSlug, practice]) => {
      if (!practice || typeof practice !== "object" || !("years" in practice) || !practice.years) {
        return practices;
      }

      const validYears = Object.entries(practice.years)
        .filter(([year]) => Number(year) >= minYear)
        .reduce(
          (yearsAccumulator, [year, yearData]) => ({
            ...yearsAccumulator,
            [year]: yearData,
          }),
          {}
        );

      return {
        ...practices,
        [practiceSlug]: {
          ...practice,
          years: validYears,
        },
      };
    }, {});

    return {
      ...filteredData,
      [fieldId]: {
        ...singleField,
        fieldOutcomes: filteredPractices,
      },
    };
  }, {} as CarbonProgramOutcomesResponse);

export const SoilPlus3ImpactPanel = ({
  carbonProgramOutcomes,
  enrolledFieldsCount = 0,
  fieldSizes,
  progress,
  shouldShowSubmissionSuccessModalContent = false,
  onOpenBulkEnrollDrawer,
  setNavigateToMissingData,
}: SoilPlus3ImpactPanelProps) => {
  const { t } = useTranslation(["fieldTimeline"]);

  const [showPayoutDrawer, setShowPayoutDrawer] = useState(false);

  const { currentFarm } = useGetFarms();
  const {
    data: { activeFarmReportingYear = moment().year() },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { data: farmFeatureFlags } = useGetFarmFeatureFlags({ farmId: currentFarm?.id ?? "" });
  const isResilienceScoreEnabled = isFeatureActive(FEATURE_FLAG_SLUGS.RESILIENCE_SCORE, farmFeatureFlags);

  // past payment
  const { data: farmPayments } = useGetFarmPayments(true);
  const farmHasPastPayouts = farmPayments?.payments.some(({ year }) => year < activeFarmReportingYear) ?? false;

  const earliestPastPaymentYear = useMemo(
    () =>
      farmPayments?.payments.reduce((min, { year }) => (year < activeFarmReportingYear && year < min ? year : min), activeFarmReportingYear) ??
      activeFarmReportingYear,
    [farmPayments?.payments, activeFarmReportingYear]
  );

  // Generate monitoring years (single years only, no multi-year ranges)
  // Filter to only include 2026+ as earlier years have different calculation methods
  const MIN_SUPPORTED_YEAR = earliestPastPaymentYear;

  const monitoringYears = useMemo(
    () =>
      generateMonitoringYears(earliestPastPaymentYear, activeFarmReportingYear).filter((year) => year >= MIN_SUPPORTED_YEAR && year <= activeFarmReportingYear),
    [MIN_SUPPORTED_YEAR, earliestPastPaymentYear, activeFarmReportingYear]
  );

  const [selectedYear, setSelectedYear] = useState<number>(activeFarmReportingYear);

  const bodyHeight = calculateImpactPanelBodyHeight();

  // Use all outcomes (not filtered by reporting year) so we can show past years too
  const allFieldOutcomes: SingleFieldOutcomes[] = Object.values(carbonProgramOutcomes).filter(isSingleFieldOutcomes);

  // Check if user has ever received a payout prediction for any field
  const hasEverReceivedPayoutPrediction = allFieldOutcomes.some((field) =>
    Object.values(PRACTICE_SLUG).some((practiceSlug) => {
      const practiceOutcome = field.fieldOutcomes?.[practiceSlug];
      if (!practiceOutcome?.isParticipating || !practiceOutcome?.years) return false;

      return Object.values(practiceOutcome.years).some(
        (yearData) =>
          (yearData as ValidAggregation)?.validation?.valid &&
          !(yearData as ValidAggregation).isBaselineYear &&
          typeof (yearData as ValidAggregation)?.payouts?.avg === "number"
      );
    })
  );

  // Show empty state for intervention farmers who have been added to a carbon program
  // but have never received a payout prediction for any field

  const hasEnrolledFields = enrolledFieldsCount > 0;
  const showEmptyState = !!currentFarm?.carbonProgram && !hasEverReceivedPayoutPrediction && !shouldShowSubmissionSuccessModalContent;

  const handleStartAddingData = () => {
    if (!hasEnrolledFields && onOpenBulkEnrollDrawer) {
      onOpenBulkEnrollDrawer();
    } else if (setNavigateToMissingData) {
      setNavigateToMissingData(true);
    }
  };

  const { fieldsContributingToPayouts, fieldsWithNonFatalErrors, totalFields } = calculateFieldContributionStatus(
    carbonProgramOutcomes ?? {},
    activeFarmReportingYear
  );

  // Fetch allotment data
  const pointPrice = currentFarm?.carbonProgram?.fieldsManagement.payouts?.removals?.pointPrice ?? 0;
  const soilPlus3Data = allFieldOutcomes.find((field) => field.soilPlus3Data)?.soilPlus3Data; // just incase some fields have some errors from the carbon runner

  const { bonusPayout, eligiblePayout, previousEligiblePayout, isAllotmentAcknowledged } = useMemo(() => {
    const isAcknowledged = !!soilPlus3Data?.acknowledgedAt;
    const payouts = calculateAllotmentPayouts(
      soilPlus3Data?.errAllotment ?? 0,
      soilPlus3Data?.bonusAllotmentAmount ?? 0,
      pointPrice,
      soilPlus3Data?.previousErrAllotment
    );

    return {
      ...payouts,
      isAllotmentAcknowledged: isAcknowledged,
    };
  }, [soilPlus3Data?.errAllotment, soilPlus3Data?.bonusAllotmentAmount, soilPlus3Data?.previousErrAllotment, soilPlus3Data?.acknowledgedAt, pointPrice]);

  const hasErrorData = useMemo(
    () => someOutcomeHasErrorOfType(carbonProgramOutcomes ?? {}, CARBON_RUNNER_ERROR_TYPE.FOOTPRINT_SERVICE),
    [carbonProgramOutcomes]
  );

  // Compute payout and CO₂ data for the selected year
  const selectedYearIsFuture = checkIsFutureYear(selectedYear, activeFarmReportingYear);
  const selectedYearIsPastPoP = isPastPeriodOfPerformance(selectedYear, activeFarmReportingYear);
  const selectedYearHasDataEntry = hasDataEntryForYear(allFieldOutcomes, selectedYear, true);
  const selectedYearPaymentStatus = farmPayments?.payments.find(({ year }) => year === selectedYear)?.status;

  const impact = useCarbonProgramImpact(carbonProgramOutcomes, selectedYear);

  const totalEnrolledHa = useMemo(() => calculateTotalEnrolledHa(allFieldOutcomes, fieldSizes), [allFieldOutcomes, fieldSizes]);

  const payoutStatus = determinePayoutStatus({
    payoutMin: impact.totalPayoutMin,
    eligible: eligiblePayout,
    isFutureYear: selectedYearIsFuture,
    hasDataEntry: selectedYearHasDataEntry,
    isPastPoP: selectedYearIsPastPoP,
    paymentStatus: selectedYearPaymentStatus,
  });

  const isNegativePayout = impact.totalPayoutMin < 0 && !selectedYearIsPastPoP;

  const showCurrentPeriodEmptyState =
    !showEmptyState && !shouldShowSubmissionSuccessModalContent && selectedYear === activeFarmReportingYear && payoutStatus === "awaitingData";

  return (
    <Flex className="field-timeline-impact-panel container" data-testid="field-timeline-impact-panel" vertical={true}>
      <div className="field-timeline-impact-panel body" style={{ height: `${bodyHeight}rem` }}>
        <Flex vertical={true}>
          {/* "Your Impact" Header  */}
          <div className="impact-panel-your-impact-header">
            <Flex justify="space-between" align="center">
              <div>
                <Typography variant="h4" className="impact-panel-title">
                  {t("impactPanel.yourImpact")}
                </Typography>
                {!showEmptyState && (
                  <Typography variant="paragraph-m" className="impact-panel-subtitle">
                    {t("reportingYear")}
                  </Typography>
                )}
              </div>

              <Flex align="center" gap="0.5rem">
                {!showEmptyState && (
                  <div className="monitoring-year-select-container">
                    <Form>
                      <Select
                        key={selectedYear}
                        className="monitoring-year-select"
                        dataTestId="monitoring-year-select"
                        name="monitoringYear"
                        popupMatchSelectWidth={false}
                        onChange={(value: number) => setSelectedYear(value)}
                        initialValue={selectedYear}
                        value={selectedYear}
                      >
                        {monitoringYears.map((year, i) => (
                          <Option data-testid={`monitoring-year-option-${i}`} value={year} key={i} className="payout-years-select-option">
                            <Flex align="center">{year}</Flex>
                          </Option>
                        ))}
                      </Select>
                    </Form>
                  </div>
                )}
                <ImpactPanelCollapseToggle />
              </Flex>
            </Flex>
            <div>
              {" "}
              <div className="impact-panel-header-divider" />
            </div>
          </div>

          {/* Content  */}
          {progress === 100 ? (
            showEmptyState ? (
              <ImpactPanelEmptyState onStartAddingData={handleStartAddingData} />
            ) : showCurrentPeriodEmptyState ? (
              <ImpactPanelEmptyState variant="currentPeriod" onStartAddingData={handleStartAddingData} />
            ) : (
              <ImpactPanelContent
                shouldShowSubmissionSuccessModalContent={shouldShowSubmissionSuccessModalContent}
                hasErrorData={hasErrorData}
                carbonProgramOutcomes={carbonProgramOutcomes}
                farmId={currentFarm?.id ?? ""}
                payoutAggregation={{ min: impact.totalPayoutMin, max: impact.totalPayoutMax }}
                co2Aggregation={{ min: impact.totalCo2Min, max: impact.totalCo2Max }}
                eligiblePayout={eligiblePayout}
                previousEligiblePayout={previousEligiblePayout}
                bonusAmount={bonusPayout}
                totalEnrolledHa={totalEnrolledHa}
                selectedYearHasDataEntry={selectedYearHasDataEntry}
                payoutStatus={payoutStatus}
                isNegativePayout={isNegativePayout}
                selectedYearIsFuture={selectedYearIsFuture}
                selectedYearIsPastPoP={selectedYearIsPastPoP}
                selectedYear={selectedYear}
                reportingYear={activeFarmReportingYear}
                currency={currentFarm?.carbonProgram?.currency}
                t={t}
                fieldsContributingToPayouts={fieldsContributingToPayouts}
                fieldsWithNonFatalErrors={fieldsWithNonFatalErrors}
                totalFields={totalFields}
                eligibleCo2Allotment={impact.allotmentAmount}
                farmHasPastPayouts={!!farmHasPastPayouts}
                allotmentId={soilPlus3Data?.allotmentId}
                isAllotmentAcknowledged={isAllotmentAcknowledged}
                monitoringYears={monitoringYears}
                allFieldOutcomes={allFieldOutcomes}
                baselineFootprints={impact.baselineFootprints}
                farmPayments={farmPayments?.payments ?? []}
                isResilienceScoreEnabled={isResilienceScoreEnabled}
                onOpenAllotmentInfo={() => setShowPayoutDrawer(true)}
              />
            )
          ) : (
            <LoadingStateCard
              dataTestId="field-timeline-result-card-loading"
              className="field-timeline-result-card margin-top-200"
              containerClassName="field-timeline-result-card container"
              title={t("impactPanel.loadingTitle")}
              description={t("impactPanel.loadingDescription")}
            />
          )}
        </Flex>
      </div>
      {/* Allotment info drawer - content managed by admins via the Popups CMS, type="payout-overview" */}
      <ExtraInfo setVisible={setShowPayoutDrawer} type="payout-overview" visible={showPayoutDrawer} />
    </Flex>
  );
};

//  Helper component

type ImpactPanelContentProps = {
  shouldShowSubmissionSuccessModalContent: boolean;
  hasErrorData: boolean;
  carbonProgramOutcomes: CarbonProgramOutcomesResponse;
  farmId: string;
  payoutAggregation: { min: number; max: number };
  co2Aggregation: { min: number; max: number };
  eligiblePayout: number;
  previousEligiblePayout?: number;
  bonusAmount: number;
  totalEnrolledHa: number;
  selectedYearHasDataEntry: boolean;
  payoutStatus: ReturnType<typeof determinePayoutStatus>;
  isNegativePayout: boolean;
  selectedYearIsFuture: boolean;
  selectedYearIsPastPoP: boolean;
  selectedYear: number;
  reportingYear: number;
  currency?: CurrencyCode;
  t: TFunction;
  fieldsContributingToPayouts: number;
  fieldsWithNonFatalErrors: number;
  totalFields: number;
  eligibleCo2Allotment: number;
  farmHasPastPayouts: boolean;
  monitoringYears: number[];
  allFieldOutcomes: SingleFieldOutcomes[];
  baselineFootprints: Record<number, number>;
  farmPayments: Payment[];
  allotmentId?: string;
  isAllotmentAcknowledged?: boolean;
  isResilienceScoreEnabled: boolean;
  onOpenAllotmentInfo: () => void;
};

const ImpactPanelContent = ({
  shouldShowSubmissionSuccessModalContent,
  hasErrorData,
  carbonProgramOutcomes,
  farmId,
  payoutAggregation,
  co2Aggregation,
  eligiblePayout,
  previousEligiblePayout,
  bonusAmount,
  totalEnrolledHa,
  selectedYearHasDataEntry,
  payoutStatus,
  isNegativePayout,
  selectedYearIsFuture,
  selectedYearIsPastPoP,
  selectedYear,
  reportingYear,
  currency,
  t,
  fieldsContributingToPayouts,
  fieldsWithNonFatalErrors,
  totalFields,
  eligibleCo2Allotment = 0,
  farmHasPastPayouts = false,
  monitoringYears,
  allFieldOutcomes,
  baselineFootprints,
  farmPayments,
  allotmentId,
  isAllotmentAcknowledged = false,
  isResilienceScoreEnabled,
  onOpenAllotmentInfo,
}: ImpactPanelContentProps) => {
  const { mutate: acknowledgeAllotment } = useAddAllotmentAcknowledgements({ farmId });

  // Display the negative payout bar when eligiblePayout is positive and isNegative is true
  const isNegativePayoutDisplay = eligiblePayout > 0 && isNegativePayout;

  const handleCongratulationsBannerClose = () => {
    if (allotmentId && farmId) {
      acknowledgeAllotment({ farmId, creditAllotmentId: allotmentId });
    }
  };

  if (shouldShowSubmissionSuccessModalContent) {
    return <SubmissionSuccessModalContent carbonProgramOutcomes={carbonProgramOutcomes ?? null} farmId={farmId} variant="panel" />;
  }

  if (hasErrorData) {
    return <SoilPlus3ImpactPanelErrorView />;
  }

  const finalProps = {
    totalPayoutMin: payoutAggregation.min,
    totalPayoutMax: payoutAggregation.max,
    eligiblePayout: eligiblePayout,
    previousEligiblePayout: previousEligiblePayout,
    bonusAmount: bonusAmount,
    totalEnrolledHa: totalEnrolledHa,
    co2Actual: co2Aggregation.min,
    co2Max: co2Aggregation.max,
    co2MaxPayoutImpact: eligibleCo2Allotment,
    co2Baseline: eligibleCo2Allotment,
    co2BaselineYear: reportingYear > 0 ? reportingYear - 1 : undefined,
    payoutStatus: payoutStatus,
    isNegative: isNegativePayoutDisplay,
    isFutureYear: selectedYearIsFuture,
    hasDataEntry: selectedYearHasDataEntry,
    isPastPoP: farmHasPastPayouts && selectedYearIsPastPoP,
    minPotential: selectedYearIsFuture ? payoutAggregation.min : undefined,
    maxPotential: selectedYearIsFuture ? payoutAggregation.max : undefined,
    currency,
    t,
    fieldsContributing: fieldsContributingToPayouts,
    totalFields,
    selectedYear,
    reportingYear,
    monitoringYears,
    allFieldOutcomes,
    baselineFootprints,
    farmPayments,
    isAllotmentAcknowledged,
    isResilienceScoreEnabled,
  };

  return (
    <>
      {/** Resilience score card */}
      {isResilienceScoreEnabled && <ResilienceScoreCard selectedYear={selectedYear} farmId={farmId} />}
      {/** Expected payouts card */}
      <ExpectedPayoutsCard
        totalPayoutMin={finalProps.totalPayoutMin}
        totalPayoutMax={finalProps.totalPayoutMax}
        eligiblePayout={finalProps.eligiblePayout}
        previousEligiblePayout={finalProps.previousEligiblePayout}
        bonusAmount={finalProps.bonusAmount}
        totalEnrolledHa={finalProps.totalEnrolledHa}
        co2Actual={finalProps.co2Actual}
        co2Max={finalProps.co2Max}
        co2MaxPayoutImpact={finalProps.co2MaxPayoutImpact}
        co2Baseline={finalProps.co2Baseline}
        co2BaselineYear={finalProps.co2BaselineYear}
        payoutStatus={finalProps.payoutStatus}
        isNegative={finalProps.isNegative}
        isFutureYear={finalProps.isFutureYear}
        hasDataEntry={finalProps.hasDataEntry}
        isPastPoP={finalProps.isPastPoP}
        minPotential={finalProps.minPotential}
        maxPotential={finalProps.maxPotential}
        currency={finalProps.currency}
        t={finalProps.t}
        fieldsContributing={finalProps.fieldsContributing}
        fieldsWithNonFatalErrors={fieldsWithNonFatalErrors}
        totalFields={finalProps.totalFields}
        selectedYear={finalProps.selectedYear}
        reportingYear={finalProps.reportingYear}
        monitoringYears={finalProps.monitoringYears}
        allFieldOutcomes={finalProps.allFieldOutcomes}
        baselineFootprints={finalProps.baselineFootprints}
        farmPayments={finalProps.farmPayments}
        onCongratulationsBannerClose={handleCongratulationsBannerClose}
        isAllotmentAcknowledged={finalProps.isAllotmentAcknowledged}
        onOpenAllotmentInfo={onOpenAllotmentInfo}
      />
    </>
  );
};
